import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const DrawAnimation = ({ winner, onAnimationComplete }) => {
    const { t } = useTranslation();
    const [stage, setStage] = useState('galaxy');

    const particles = useMemo(() => Array.from({ length: 200 }).map((_, i) => ({
        id: i,
        x: `${Math.random() * 100}%`,
        y: `${Math.random() * 100}%`,
        size: `${Math.random() * 3 + 1}px`,
        duration: Math.random() * 5 + 5,
    })), []);

    useEffect(() => {
        const sequence = async () => {
            await new Promise(res => setTimeout(res, 4000)); // Stage 1: Galaxy swirls
            setStage('scan');
            await new Promise(res => setTimeout(res, 3000)); // Stage 2: Beam scans
            setStage('highlight');
            await new Promise(res => setTimeout(res, 2000)); // Stage 3: Highlight winner
            setStage('reveal');
            await new Promise(res => setTimeout(res, 3000)); // Stage 4: Reveal winner
            onAnimationComplete();
        };
        sequence();
    }, [onAnimationComplete]);

    const winnerParticleId = useMemo(() => winner ? winner.winnerId.charCodeAt(0) % particles.length : Math.floor(particles.length / 2), [winner, particles.length]);

    return (
        <div className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden z-50">
            <AnimatePresence>
                {/* Stage 1: Swirling Galaxy */}
                {stage === 'galaxy' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full h-full">
                        {particles.map(p => (
                            <motion.div
                                key={p.id}
                                className="absolute rounded-full bg-white"
                                style={{ left: p.x, top: p.y, width: p.size, height: p.size }}
                                animate={{
                                    transform: ['translateX(0px)', 'translateX(20px)', 'translateX(-20px)', 'translateX(0px)'],
                                    opacity: [0.5, 1, 0.5],
                                }}
                                transition={{ duration: p.duration, repeat: Infinity, ease: 'easeInOut' }}
                            />
                        ))}
                        <motion.p initial={{opacity: 0}} animate={{opacity: 1, transition: {delay: 1}}} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-purple-300 text-2xl font-playfair">{t('drawAnimation.readingStars')}</motion.p>
                    </motion.div>
                )}

                {/* Stage 2: Scanning Beam */}
                {stage === 'scan' && (
                    <div className="w-full h-full">
                        {particles.map(p => <div key={p.id} className="absolute rounded-full bg-gray-600" style={{ left: p.x, top: p.y, width: p.size, height: p.size }} />)}
                        <motion.div
                            className="absolute top-0 h-full w-16 bg-gradient-to-r from-transparent via-purple-400/50 to-transparent"
                            initial={{ x: '-100%' }}
                            animate={{ x: '100vw', transition: { duration: 2, ease: 'easeInOut' } }}
                        />
                        <p className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-purple-300 text-2xl font-playfair">{t('drawAnimation.aligningFate')}</p>
                    </div>
                )}

                {/* Stage 3 & 4: Highlight & Reveal */}
                {(stage === 'highlight' || stage === 'reveal') && (
                    <div className="w-full h-full">
                        {/* Non-winner particles fade out */}
                        {particles.map(p => (
                            <motion.div
                                key={p.id}
                                className="absolute rounded-full"
                                style={{ left: p.x, top: p.y, width: p.size, height: p.size, background: p.id === winnerParticleId ? 'white' : '#4A5568' }}
                                animate={{ opacity: p.id === winnerParticleId ? 1 : 0, transition: { duration: 1 } }}
                            />
                        ))}
                        
                        {/* The Winning Star */}
                        <motion.div
                            className="absolute rounded-full bg-white shadow-2xl shadow-yellow-300"
                            style={{
                                left: particles[winnerParticleId].x,
                                top: particles[winnerParticleId].y,
                                width: particles[winnerParticleId].size,
                                height: particles[winnerParticleId].size,
                            }}
                            animate={{
                                scale: stage === 'reveal' ? 300 : [1, 10, 8], // Grow, pulse, then massive grow
                                x: stage === 'reveal' ? `calc(50vw - ${particles[winnerParticleId].x})` : 0,
                                y: stage === 'reveal' ? `calc(50vh - ${particles[winnerParticleId].y})` : 0,
                                transition: { duration: 2, ease: 'circOut' }
                            }}
                        />
                        
                        {/* Reveal Winner Info */}
                        {stage === 'reveal' && winner && (
                             <motion.div 
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center"
                                initial={{opacity: 0}}
                                animate={{opacity: 1, transition: {delay: 1.5}}}
                             >
                                <img src={winner.avatarUrl || '/logo1024.png'} alt={t('drawAnimation.winnerAlt')} className="w-32 h-32 rounded-full border-4 border-yellow-400 shadow-lg mb-4"/>
                                <p className="text-xl text-gray-300">{t('drawAnimation.congratulations')}</p>
                                <h2 className="text-5xl font-bold text-yellow-300 font-playfair">{winner.displayName || t('drawAnimation.fortunateSoul')}</h2>
                             </motion.div>
                        )}
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default DrawAnimation;
